import { useMutation, UseMutationOptions } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";

type EnableTwoFactorData = { method: "otp" | "totp"; totpURI: string; backupCodes: string[] };

/**
 * Starts TOTP enrolment. Returns the `totpURI` (render as a QR code) and one-time `backupCodes`.
 * The user isn't enrolled until they verify a code with useVerifyTotp — `twoFactorEnabled` only
 * flips true server-side after a successful verify.
 * @param options
 * @returns
 */
export function useEnableTwoFactor(
  options?: UseMutationOptions<EnableTwoFactorData, unknown, { password: string }, unknown>,
) {
  return useMutation({
    mutationFn: async ({ password }: { password: string }): Promise<EnableTwoFactorData> => {
      const { data, error } = await authClient.twoFactor.enable({ password, method: "totp" });
      if (error) {
        throw new Error(betterAuthErrorText(error, "Could not start two-factor setup"));
      }
      // We only support TOTP, so the OTP variant should be unreachable — guard rather than assert.
      if (data.method !== "totp") {
        throw new Error("Unexpected two-factor method returned by the server");
      }
      return data;
    },
    ...options,
  });
}
